import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import closeIcon from '../../images/common/close.svg';
import StyledTappable from './StyledTappable';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';

import downloadIcon from '../../images/consultations/download-dark.svg';
import { useDispatch, useSelector } from 'react-redux';
import { closeFileViewer } from '../../actions';
import LoadingComponent from './LoadingComponent';
import Modal from '../../containers/common/Modal';
import { FlyInTransition } from '../transitions/transitions';
import { getBlobAndBufferAndContentType } from './Utils';

const OuterContainer = styled.div`
  background: #fff;
  border-radius: 12px 12px 0px 0px;
  height: 95%;
  width: 440px;
  position: absolute;
  z-index: 100;
  bottom: 0;
  padding: 12px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  @media only screen and (max-width: 440px) {
    height: 100%;
    max-width: 100vw;
  }
`;
const Header = styled.div`
  font-family: 14px;
  font-weight: 500;
  width: 100%;
  padding: 10px;
  padding-top: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f3f6;
`;
const CloseContainer = styled(StyledTappable)`
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 4px;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Btn = styled.div`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  width: fit-content;
  font-size: 14px;
  height: 24px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.1s ease;
  &:hover {
    opacity: 0.6;
  }
  &:active {
    opacity: 0.3;
  }
`;
const FileContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 800px;
  overflow: auto; /* Make the container scrollable */
  @media only screen and (max-width: 440px) {
    max-width: 420px;
  }
`;
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  align-items: center;
  padding: 4px;
  background: #ddd;
  min-height: 500px;
  width: 100%;
  max-width: 800px;
  padding-bottom: 80px;
  @media only screen and (max-width: 440px) {
    max-width: 420px;
  }
`;
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const FileRender = ({ pdfUrl, pdfData, numPages, setNumPages }) => {
  const screenWidth = window.innerWidth;
  const [pageNumber, setPageNumber] = useState(1);
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <div className="h-full">
      <FileContainer>
        <Document
          file={pdfData}
          renderTextLayer={false} // Disable text layer
          renderAnnotationLayer={false} // Disable annotation layer
          width={screenWidth > 800 ? '800' : screenWidth}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <PageContainer>
            {[...Array(numPages)].map((_, index) => (
              <Page
                zoom={1}
                width={screenWidth > 800 ? '780' : screenWidth - 20}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                style={{ width: '100vw' }}
              />
            ))}
          </PageContainer>
          <p>
            Page {pageNumber} of {numPages}
          </p>
        </Document>
      </FileContainer>
    </div>
  );
};

const FileViewerModal = ({ url, close }) => {
  const appFileViewer = useSelector((state) => state?.app?.appFileViewer);
  console.log('appFileViewer', appFileViewer);
  const screenWidth = window.innerWidth;
  let isOpen = null,
    title = 'PDF Viewer',
    contentUrl = null,
    type = 'pdf';

  if (appFileViewer) {
    isOpen = appFileViewer.isOpen;
    title = appFileViewer.title;
    contentUrl = appFileViewer.contentUrl;
    type = appFileViewer.type;
  }
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [errorText, setErrorText] = useState('');
  const authToken = useSelector((state) => state.user.authToken);
  const [data, setData] = useState({
    buffer: null,
    type: null,
    blobUrl: null,
    fileKind: null,
  });
  const [numPages, setNumPages] = useState(null);

  const downloadFile = () => {
    if (!data?.blobUrl) return;

    const contentType = data['type']
      ? `${data['type'].replace('application/', '').replace('image/', '')}`
      : String(data.blobUrl).split('.').pop();

    const a = document.createElement('a');
    a.href = data.blobUrl;
    a.download = String(contentType).toLowerCase().includes('octet-stream')
      ? String(data?.fileKind).toLowerCase().includes('pdf')
        ? `VISIT-${Date.now()}.pdf`
        : `VISIT-${Date.now()}.png`
      : `VISIT-${Date.now()}.${contentType}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const fetchDocument = async () => {
    setError(false);
    setFetching(true);
    setErrorText('');
    try {
      const { buffer, contentType, blobUrl, fileKind } =
        await getBlobAndBufferAndContentType({
          fileUrl: contentUrl,
          headers: {
            Authorization: authToken,
          },
        });
      setData({
        buffer: buffer,
        type: contentType,
        blobUrl: blobUrl,
        fileKind: fileKind,
      });
      setError(false);
      setErrorText('');
    } catch (error) {
      console.error('Error fetching Document:', error);
      setError(true);
      setErrorText(error.message);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchDocument();
  }, []);

  if (!isOpen) return null;

  // Resolve the file kind from the actual fetched content first, then the URL
  // extension, and only fall back to the caller-supplied `type` (which is often
  // hardcoded to 'pdf'). This lets the viewer render images served by
  // authenticated endpoints like `/document/fetch-document-content?...&extension=.jpeg`.
  const getUrlExtension = (rawUrl) => {
    if (!rawUrl) return '';
    const queryMatch = String(rawUrl).match(/[?&]extension=\.?([a-z0-9]+)/i);
    if (queryMatch) return queryMatch[1].toLowerCase();
    const path = String(rawUrl).split('?')[0];
    return path.includes('.') ? path.split('.').pop().toLowerCase() : '';
  };
  const urlExtension = getUrlExtension(contentUrl);
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

  const isImage =
    data?.fileKind === 'image' ||
    String(data?.type).toLowerCase().startsWith('image/') ||
    imageExtensions.includes(urlExtension);

  const isPdf =
    !isImage &&
    (data?.fileKind === 'pdf' ||
      String(data?.type).toLowerCase().includes('pdf') ||
      urlExtension === 'pdf' ||
      type === 'pdf');

  function handleClose() {
    if (data?.blobUrl) URL.revokeObjectURL(data.blobUrl);
    setData({ buffer: null, type: null, blobUrl: null, fileKind: null });
    dispatch(closeFileViewer());
  }
  return (
    <Modal>
      <div className="modalContainer">
        <FlyInTransition in={isOpen} mountOnEnter unmountOnExit appear>
          <OuterContainer>
            <Header>
              <div className="flex flex-row items-center gap-2 text-body-l text-txtlight-new font-semibold">
                <p className=" ">{title}</p>
                {!fetching && !error && appFileViewer?.showDownload && (
                  <Btn onClick={downloadFile}>
                    <img
                      src={downloadIcon}
                      alt="download-icon"
                      style={{ height: '16px' }}
                    />
                    <span>Download</span>
                  </Btn>
                )}
                {numPages ? <p>Pages: {numPages}</p> : <></>}
              </div>

              <CloseContainer onTap={handleClose}>
                <img src={closeIcon} alt="close" />
              </CloseContainer>
            </Header>
            {fetching && !error && <LoadingComponent />}
            {error && (
              <div className="text-body-l mt-4">
                Some Error occurred in fetching prescription:
                <p className="text-body-l">{errorText}</p>
              </div>
            )}
            {type === 'pdf' && data?.buffer && (
              <a
                id="prescription-download1"
                download={'visit-prescription.pdf'}
                href={URL.createObjectURL(data?.buffer)}
                className="hidden"
              />
            )}
            {type !== 'pdf' && (
              <a
                id="prescription-download2"
                download={'visit-prescription'}
                href={data?.buffer}
                className="hidden"
              />
            )}
            {/* "application/octet-stream"
             */}

            {!fetching && isPdf ? (
              data?.buffer ? (
                <FileRender
                  pdfData={data.buffer}
                  pdfUrl={data.blobUrl}
                  setNumPages={setNumPages}
                  numPages={numPages}
                />
              ) : (
                <FileContainer>
                  <iframe
                    src={data.blobUrl || contentUrl}
                    className="w-full border-0"
                    style={{ height: '85vh' }}
                    title="PDF Preview"
                  />
                </FileContainer>
              )
            ) : (
              !fetching &&
              (data?.blobUrl || contentUrl) && (
                <img
                  src={data?.blobUrl || contentUrl}
                  width={screenWidth > 800 ? '750' : screenWidth - 20}
                  alt="content"
                  onError={(e) => {
                    const img = e.currentTarget;
                    if (
                      !img.dataset.fallback &&
                      contentUrl &&
                      img.src !== contentUrl
                    ) {
                      img.dataset.fallback = '1';
                      img.src = contentUrl;
                    }
                  }}
                />
              )
            )}
          </OuterContainer>
        </FlyInTransition>
      </div>
    </Modal>
  );
};

export default FileViewerModal;
